import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Plus, Save, Trash2, Sliders, X, BarChart3 } from "lucide-react";
import { Card, CardHead, Btn, Badge, Spinner } from "@/components/ui-kit";
import api from "@/lib/api";

const REHAB_TYPES = ["Orthopedic", "Neuro", "Sports", "Cardiac", "General"];
const CLINICIANS = ["Standard", "Senior", "Specialist"];
const LOCATIONS = ["Metro", "Tier 1", "Tier 2"];

const BLANK = {
  name: "", description: "", rehab_type: "Orthopedic", base_price: 4000, per_session: 800,
  sessions: 12, weeks: 6, equipment_fee: 1500, home_visit_fee: 2000, override_formula: false,
  clinician_multiplier: { Standard: 1.0, Senior: 1.3, Specialist: 1.6 },
  location_multiplier: { Metro: 1.2, "Tier 1": 1.0, "Tier 2": 0.9 },
  specialisation_multiplier: {}, active: true,
};

function Field({ label, children }) {
  return <label className="block"><span className="pq-label block mb-1.5">{label}</span>{children}</label>;
}

function PackageForm({ pkg, currency, onClose, onSaved }) {
  const [f, setF] = useState({ ...BLANK, ...pkg });
  const [saving, setSaving] = useState(false);
  const [specName, setSpecName] = useState("");
  const [specVal, setSpecVal] = useState("");
  const [sim, setSim] = useState({ clinician: "Specialist", location: "Metro", sessions: f.sessions, equipment: true, home: false });
  const set = (k, num = false) => (e) => setF({ ...f, [k]: num ? Number(e.target.value) : e.target.value });
  const setMul = (field, key) => (e) => setF({ ...f, [field]: { ...f[field], [key]: Number(e.target.value) } });

  const preview = () => {
    const base = Number(f.base_price || 0);
    const eq = sim.equipment ? Number(f.equipment_fee || 0) : 0;
    const hm = sim.home ? Number(f.home_visit_fee || 0) : 0;
    if (f.override_formula) return base + eq + hm;
    const cm = Number(f.clinician_multiplier?.[sim.clinician] ?? 1);
    const lm = Number(f.location_multiplier?.[sim.location] ?? 1);
    const sc = Number(f.per_session || 0) * Number(sim.sessions || 0);
    return Math.round((base + sc) * cm * lm + eq + hm);
  };
  const money = (n) => `${currency}${Number(n || 0).toLocaleString("en-IN")}`;

  const save = async () => {
    if (!f.name.trim()) return toast.error("Package name is required");
    setSaving(true);
    try {
      const payload = { ...f, base_price: Number(f.base_price), per_session: Number(f.per_session),
        sessions: Number(f.sessions), weeks: Number(f.weeks), equipment_fee: Number(f.equipment_fee),
        home_visit_fee: Number(f.home_visit_fee) };
      if (pkg?.id) await api.put(`/admin/packages/${pkg.id}`, payload);
      else await api.post("/admin/packages", payload);
      toast.success(pkg?.id ? "Package updated" : "Package created");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="package-form-modal">
      <Card className="w-full max-w-2xl my-6">
        <CardHead title={pkg?.id ? "Edit Treatment Package" : "New Treatment Package"}
          action={<button onClick={onClose} data-testid="package-form-close" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>} />
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Package name"><input className="pq-input" value={f.name} onChange={set("name")} data-testid="pkg-name" /></Field>
            <Field label="Rehab type">
              <select className="pq-input" value={f.rehab_type} onChange={set("rehab_type")} data-testid="pkg-rehab">
                {REHAB_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description"><input className="pq-input" value={f.description} onChange={set("description")} data-testid="pkg-desc" /></Field>

          <label className="flex items-center gap-2 text-[13px] font-medium bg-slate-50 rounded-lg px-3 py-2.5">
            <input type="checkbox" className="accent-teal-600" checked={f.override_formula} onChange={(e) => setF({ ...f, override_formula: e.target.checked })} data-testid="pkg-override" />
            Fixed price — override the rate formula (ignore sessions & multipliers)
          </label>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label={`Base price (${currency})`}><input type="number" className="pq-input metric" value={f.base_price} onChange={set("base_price", true)} data-testid="pkg-base" /></Field>
            <Field label={`Per-session (${currency})`}><input type="number" className="pq-input metric" value={f.per_session} onChange={set("per_session", true)} disabled={f.override_formula} data-testid="pkg-persession" /></Field>
            <Field label="Sessions"><input type="number" className="pq-input metric" value={f.sessions} onChange={set("sessions", true)} data-testid="pkg-sessions" /></Field>
            <Field label="Weeks"><input type="number" className="pq-input metric" value={f.weeks} onChange={set("weeks", true)} data-testid="pkg-weeks" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={`Equipment fee (${currency})`}><input type="number" className="pq-input metric" value={f.equipment_fee} onChange={set("equipment_fee", true)} data-testid="pkg-equip" /></Field>
            <Field label={`Home-visit fee (${currency})`}><input type="number" className="pq-input metric" value={f.home_visit_fee} onChange={set("home_visit_fee", true)} data-testid="pkg-home" /></Field>
          </div>

          {!f.override_formula && (
            <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="pq-label mb-1.5">Clinician / seniority modifier</div>
                <div className="grid grid-cols-3 gap-2">
                  {CLINICIANS.map((c) => <Field key={c} label={c}><input type="number" step="0.05" className="pq-input metric" value={f.clinician_multiplier?.[c] ?? 1} onChange={setMul("clinician_multiplier", c)} data-testid={`pkg-cmul-${c}`} /></Field>)}
                </div>
              </div>
              <div>
                <div className="pq-label mb-1.5">Location modifier</div>
                <div className="grid grid-cols-3 gap-2">
                  {LOCATIONS.map((l) => <Field key={l} label={l}><input type="number" step="0.05" className="pq-input metric" value={f.location_multiplier?.[l] ?? 1} onChange={setMul("location_multiplier", l)} data-testid={`pkg-lmul-${l.replace(/\s/g, "")}`} /></Field>)}
                </div>
              </div>
            </div>
            <div>
              <div className="pq-label mb-1.5">Specialisation modifiers</div>
              <div className="space-y-2">
                {Object.entries(f.specialisation_multiplier || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2" data-testid={`pkg-spec-${k}`}>
                    <span className="flex-1 text-[13px] font-medium bg-slate-50 rounded-lg px-3 py-2">{k}</span>
                    <input type="number" step="0.05" className="pq-input metric max-w-[100px]" value={v}
                      onChange={setMul("specialisation_multiplier", k)} data-testid={`pkg-specmul-${k}`} />
                    <Btn size="sm" variant="danger" onClick={() => { const m = { ...f.specialisation_multiplier }; delete m[k]; setF({ ...f, specialisation_multiplier: m }); }} data-testid={`pkg-spec-remove-${k}`}><X size={13} /></Btn>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <input className="pq-input flex-1" placeholder="e.g. Sports, Post-Surgical, Paediatric" value={specName}
                    onChange={(e) => setSpecName(e.target.value)} data-testid="pkg-spec-name" />
                  <input type="number" step="0.05" className="pq-input metric max-w-[100px]" placeholder="1.15" value={specVal}
                    onChange={(e) => setSpecVal(e.target.value)} data-testid="pkg-spec-value" />
                  <Btn size="sm" onClick={() => {
                    if (!specName.trim()) return;
                    setF({ ...f, specialisation_multiplier: { ...f.specialisation_multiplier, [specName.trim()]: Number(specVal) || 1 } });
                    setSpecName(""); setSpecVal("");
                  }} data-testid="pkg-spec-add"><Plus size={13} /> Add</Btn>
                </div>
              </div>
              <p className="text-[11.5px] text-slate-400 mt-1.5">Applied on top of clinician & location modifiers when the order's specialisation matches.</p>
            </div>
            </>
          )}

          <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-4">
            <div className="flex items-center gap-2 mb-3 text-teal-700 font-semibold text-[13px]"><Sliders size={15} /> Live price preview (what-if)</div>
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Clinician"><select className="pq-input py-1.5" value={sim.clinician} onChange={(e) => setSim({ ...sim, clinician: e.target.value })} data-testid="pkg-sim-clinician">{CLINICIANS.map((c) => <option key={c}>{c}</option>)}</select></Field>
              <Field label="Location"><select className="pq-input py-1.5" value={sim.location} onChange={(e) => setSim({ ...sim, location: e.target.value })} data-testid="pkg-sim-location">{LOCATIONS.map((l) => <option key={l}>{l}</option>)}</select></Field>
              <Field label="Sessions"><input type="number" className="pq-input py-1.5 max-w-[80px] metric" value={sim.sessions} onChange={(e) => setSim({ ...sim, sessions: e.target.value })} data-testid="pkg-sim-sessions" /></Field>
              <label className="flex items-center gap-1.5 text-[12.5px] font-medium mb-2"><input type="checkbox" className="accent-teal-600" checked={sim.equipment} onChange={(e) => setSim({ ...sim, equipment: e.target.checked })} /> Equip</label>
              <label className="flex items-center gap-1.5 text-[12.5px] font-medium mb-2"><input type="checkbox" className="accent-teal-600" checked={sim.home} onChange={(e) => setSim({ ...sim, home: e.target.checked })} /> Home</label>
              <div className="ml-auto text-right"><div className="pq-label">Order price</div><div className="metric text-2xl font-extrabold text-teal-700" data-testid="pkg-preview-amount">{money(preview())}</div></div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200">
          <Btn onClick={onClose} data-testid="pkg-cancel">Cancel</Btn>
          <Btn variant="primary" onClick={save} disabled={saving} data-testid="pkg-save">{saving ? <Spinner className="w-4 h-4" /> : <><Save size={15} /> {pkg?.id ? "Update" : "Create"} package</>}</Btn>
        </div>
      </Card>
    </div>
  );
}

export default function Packages({ currency = "₹" }) {
  const [pkgs, setPkgs] = useState(null);
  const [editing, setEditing] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const load = useCallback(() => api.get("/admin/packages").then((r) => setPkgs(r.data)), []);
  const loadAnalytics = useCallback(() => api.get("/admin/package-analytics").then((r) => setAnalytics(r.data)), []);
  useEffect(() => { load(); loadAnalytics(); }, [load, loadAnalytics]);
  const money = (n) => `${currency}${Number(n || 0).toLocaleString("en-IN")}`;

  const remove = async (p) => {
    if (!window.confirm(`Permanently delete "${p.name}"? This cannot be undone.`)) return;
    await api.delete(`/admin/packages/${p.id}`);
    toast.success("Package deleted"); load(); loadAnalytics();
  };

  if (!pkgs) return <div className="flex justify-center py-16"><Spinner className="w-6 h-6 text-teal-600" /></div>;

  const totalOrders = analytics?.packages?.reduce((s, a) => s + a.orders, 0) || 0;

  return (
    <>
      {analytics && analytics.packages.length > 0 && (
        <Card className="mb-5" data-testid="package-analytics-card">
          <CardHead title={<span className="flex items-center gap-2"><BarChart3 size={17} className="text-teal-600" /> Package Analytics</span>}
            action={<Badge tone="slate">{totalOrders} orders</Badge>} />
          <div className="overflow-x-auto"><table className="w-full">
            <thead><tr>{["Package", "Orders", "Order mix", "Gross revenue"].map((h) => <th key={h} className="pq-label px-4 py-3 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
            <tbody>
              {analytics.packages.map((a) => (
                <tr key={a.package_id} className="border-b border-slate-100 last:border-0 text-[13px]" data-testid={`pkg-analytics-${a.package_id}`}>
                  <td className="px-4 py-3 font-semibold">{a.name}</td>
                  <td className="px-4 py-3 metric">{a.orders}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-teal-500" style={{ width: `${totalOrders ? (a.orders / totalOrders) * 100 : 0}%` }} /></div>
                      <span className="text-[11.5px] text-slate-500">{totalOrders ? Math.round((a.orders / totalOrders) * 100) : 0}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 metric">{money(a.gross)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </Card>
      )}

      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-display font-semibold text-lg text-slate-800">Treatment Packages</h3>
          <p className="text-[13px] text-slate-500">Create, price and manage packages used across the platform.</p>
        </div>
        <Btn variant="primary" onClick={() => setEditing({})} data-testid="add-package-btn"><Plus size={15} /> New package</Btn>
      </div>

      {pkgs.length === 0 ? (
        <Card className="p-10 text-center text-slate-500 text-sm">No packages yet. Create your first treatment package.</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {pkgs.map((p) => (
            <Card key={p.id} hover className={`p-5 ${p.active ? "" : "opacity-60"}`} data-testid={`package-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center"><Package size={19} /></div>
                <div className="flex gap-1">
                  <Badge tone="slate">{p.rehab_type}</Badge>
                  {p.override_formula ? <Badge tone="amber">Fixed</Badge> : <Badge tone="indigo">Dynamic</Badge>}
                  {!p.active && <Badge tone="rose">Archived</Badge>}
                </div>
              </div>
              <div className="font-display font-semibold text-slate-800 text-[15px]">{p.name}</div>
              <p className="text-[12.5px] text-slate-500 mt-1 line-clamp-2 min-h-[34px]">{p.description}</p>
              <div className="grid grid-cols-2 gap-2 mt-3 text-center">
                <div className="bg-slate-50 rounded-lg py-2"><div className="text-[11px] text-slate-500">Base</div><div className="metric font-bold text-[13px]">{money(p.base_price)}</div></div>
                <div className="bg-slate-50 rounded-lg py-2"><div className="text-[11px] text-slate-500">Sessions</div><div className="metric font-bold text-[13px]">{p.sessions} · {p.weeks}w</div></div>
              </div>
              <div className="flex gap-2 mt-4">
                <Btn size="sm" className="flex-1" onClick={() => setEditing(p)} data-testid={`edit-package-${p.id}`}>Edit</Btn>
                <Btn size="sm" variant="danger" onClick={() => remove(p)} data-testid={`archive-package-${p.id}`}><Trash2 size={14} /></Btn>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && <PackageForm pkg={editing} currency={currency} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}
